import { useMemo } from 'react';
import { createColumnHelper, type Row } from '@tanstack/table-core';
import {
  Badge,
  DataTable,
  InlineStack,
  Link,
  Stack,
  Text,
  type DataTableFeatures,
} from '@customerio/pluma-components/react';
import { PlatformIcon, PLATFORM_LABELS } from '@/features/push-credentials';
import { VIEWER_RESTRICTED_MESSAGE } from '@/utils/viewer-restricted';
import type { App, AppPlatform } from '@/services/db/app/entity';

const ALREADY_DEFAULT_MESSAGE = 'This app is already your workspace default.';

const columnHelper = createColumnHelper<DataTableFeatures, App>();

export type AppsTableProps = {
  apps: App[];
  isLoading: boolean;
  canEdit: boolean;
  canDelete: boolean;
  isDeleting: boolean;
  getEditHref: (app: App) => string;
  onSetDefault: (app: App) => void;
  onDelete: (app: App) => void;
};

export function AppsTable({
  apps,
  isLoading,
  canEdit,
  canDelete,
  isDeleting,
  getEditHref,
  onSetDefault,
  onDelete,
}: AppsTableProps) {
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'name',
        header: 'Name',
        cell: (info) => <NameCell app={info.row.original} href={getEditHref(info.row.original)} />,
      }),
      columnHelper.display({
        id: 'identifier',
        header: 'App identifier',
        cell: (info) => <IdentifierCell app={info.row.original} />,
        meta: { width: 360 },
      }),
    ],
    [getEditHref],
  );

  const rowActions = (row: Row<DataTableFeatures, App>) => {
    const app = row.original;
    const editTooltip = canEdit ? undefined : VIEWER_RESTRICTED_MESSAGE;
    return {
      dropdownTrigger: { isDisabled: !canEdit && !canDelete, ariaLabel: `Actions for ${app.name}` },
      dropdownActions: [
        {
          label: 'Edit',
          icon: 'edit' as const,
          isDisabled: !canEdit,
          tooltip: editTooltip,
          href: getEditHref(app),
        },
        {
          label: 'Set as default app…',
          icon: 'star' as const,
          isDisabled: app.is_default || !canEdit,
          tooltip: app.is_default ? ALREADY_DEFAULT_MESSAGE : editTooltip,
          onClick: () => onSetDefault(app),
        },
        {
          label: 'Delete…',
          icon: 'delete-trash' as const,
          isCritical: true,
          isDisabled: !canDelete || isDeleting,
          tooltip: canDelete ? undefined : VIEWER_RESTRICTED_MESSAGE,
          onClick: () => onDelete(app),
        },
      ],
    };
  };

  return <DataTable caption="Apps" columns={columns} data={apps} isLoading={isLoading} rowActions={rowActions} />;
}

type NameCellProps = {
  app: App;
  href: string;
};

function NameCell({ app, href }: NameCellProps) {
  return (
    <InlineStack gap="100" alignBlock="center">
      <Link href={href}>{app.name}</Link>
      {app.is_default ? <Badge color="grey">Default</Badge> : null}
    </InlineStack>
  );
}

type IdentifierCellProps = {
  app: App;
};

function IdentifierCell({ app }: IdentifierCellProps) {
  return (
    <Stack gap="050">
      {app.platforms.map((platform) => (
        <PlatformIdentifier key={platform.id} platform={platform} />
      ))}
    </Stack>
  );
}

type PlatformIdentifierProps = {
  platform: AppPlatform;
};

/**
 * The mark is labelled rather than decorative: an app often reports the same
 * identifier on both platforms, so nothing else tells the two lines apart.
 */
function PlatformIdentifier({ platform }: PlatformIdentifierProps) {
  const name = platform.platform === 'ios' ? 'ios' : 'android';
  return (
    <InlineStack gap="050" alignBlock="center" minW="0">
      <PlatformIcon platform={name} label={PLATFORM_LABELS[name]} />
      <Text truncate={true} minW="0">
        {platform.identifier}
      </Text>
    </InlineStack>
  );
}
